import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon, EyeIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router';
import * as yup from 'yup';

import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { MODAL_DOCUMENT_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectDatasourceId, selectDocumentId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { COLLECTIONS_WEBPAGE_SOURCE_DOCS_URL } from '@/util/constants';
import { hasDirtyFields, useHasDirtyFields } from '@/util/util';

import { DOCUMENT_TYPES_WITH_PREVIEW } from '../../constants';
import { getPureName } from '../../helper';
import { useAPIDatasourcesDocuments } from '../../hooks/useAPIDatasourcesDocuments';
import { useAPIDocuments } from '../../hooks/useAPIDocuments';
import { useTabCategory } from '../../hooks/useCategory';
import { editDocumentSchema, checkDocumentNameAvailability } from '../../validator';

type FormValues = yup.InferType<ReturnType<typeof editDocumentSchema>>;

export const EditDocumentForm = () => {
  const dispatch = useDispatch();
  // Redux selectors
  const datasourceIdFromRedux = useSelector(selectDatasourceId);
  const { datasourceId: datasourceIdParam } = useParams();
  const documentId = useSelector(selectDocumentId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const datasourceId = datasourceIdParam || datasourceIdFromRedux;

  // Hooks
  const { t } = useTranslation();
  const { collectionId } = useParams();
  const { category } = useTabCategory();

  const { getDocumentById } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceId,
    document_id: documentId,
  });
  const { getDatasourceDocumentById } = useAPIDatasourcesDocuments({
    collection_id: collectionId,
    datasourceTab: category,
  });
  const documentFromOutsideFolder = getDatasourceDocumentById(documentId);
  const documentFromInsideFolder = getDocumentById(documentId);
  const document = documentFromInsideFolder || documentFromOutsideFolder;

  const { updateDocument } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceId,
    document_id: documentId,
  });

  // Local state
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // RHF
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(
      editDocumentSchema(
        collectionId || '',
        datasourceId || '',
        documentId || undefined,
        getPureName(document?.name)
      )
    ),
    values: { title: getPureName(document?.name) || '', externalUrl: document?.external_url || '' },
  });

  // Local variables
  const isSubmitting = form.formState.isSubmitting;

  // Check if preview should be shown
  const showPreviewOption = document && DOCUMENT_TYPES_WITH_PREVIEW.includes(document.type);

  const resetReduxState = () => {
    dispatch(setDocumentId(null));
    dispatch(setDatasourceId(null));
  };

  // Handlers
  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      dispatch(setSidebarContent(null));
      resetReduxState();
    }
  };

  const handlePreview = () => {
    if (!document || !datasourceId || !collectionId) {
      return;
    }

    dispatch(
      pushModal(MODAL_DOCUMENT_PREVIEW, {
        document_id: document.document_id,
        datasource_id: datasourceId,
        collection_id: collectionId,
      })
    );
  };

  const onSubmit = async (data: FormValues) => {
    const originalName = getPureName(document?.name);
    const newName = data.title.trim();
    const nameHasChanged = !originalName || newName.toLowerCase() !== originalName.toLowerCase();

    // Only validate name for file-based documents (where file_url is not null) and when name has changed
    if (document?.file_url && nameHasChanged) {
      try {
        const nameCheckResult = await checkDocumentNameAvailability({
          collectionId,
          datasourceId,
          name: data.title,
          documentId,
        });

        if (!nameCheckResult.available) {
          form.setError('title', {
            type: 'manual',
            message: nameCheckResult.message || 'This name is already taken',
          });
          return;
        }
      } catch {
        // Handle unexpected errors during name validation
        form.setError('title', { type: 'manual', message: t('validation.error_checking_name') });
        return;
      }
    }

    // I want to update the external url only when it is not empty
    const externalUrl = data.externalUrl ? data.externalUrl : document?.external_url;

    updateDocument({
      document_id: documentId,
      name: data.title,
      external_url: externalUrl,
      type: document?.type,
    });
  };

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'edit-document-form' });
  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {t('collections.documents.edit_document.title')}
        </h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1">
            <fieldset disabled={isSubmitting} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('collections.documents.edit_document.document_title')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        autoFocus={canWrite}
                        {...field}
                        readOnly={!canWrite}
                        onChange={(e) => {
                          field.onChange(e);
                          form.trigger('title');
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="externalUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('collections.documents.edit_document.external_url_optional')}
                    </FormLabel>
                    <FormControl>
                      <URLInput
                        value={field.value}
                        onChange={field.onChange}
                        readOnly={!canWrite}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      {t('collections.documents.edit_document.external_url_optional_description')}{' '}
                      <Link
                        to={COLLECTIONS_WEBPAGE_SOURCE_DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-surface-button-primary"
                      >
                        {t('common.learn_more')}
                      </Link>
                    </FormDescription>
                  </FormItem>
                )}
              />

              {showPreviewOption && (
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handlePreview}
                    disabled={isSubmitting}
                    className="justify-start px-0 hover:bg-transparent hover:underline"
                  >
                    <EyeIcon size={16} />
                    {t('common.preview')}
                  </Button>
                </div>
              )}
            </fieldset>
          </SidebarWrapperContent>

          {canWrite && (
            <SidebarWrapperFooter>
              <Button aria-label="submit-edit-document-form" type="submit" disabled={isSubmitting}>
                {t('common.update')}
              </Button>
              <Button
                type="button"
                variant="ghost-neutral"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
            </SidebarWrapperFooter>
          )}
        </SidebarWrapperForm>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            dispatch(setSidebarContent(null));
            resetReduxState();
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
